import debounce from 'lodash.debounce'
import { shallowEqual } from 'react-redux'
import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useAppSelector } from 'state/store'
import { ActivityValue, NestableGroupValue } from 'types/shared'
import { FilteractionParams } from 'utils/apiTypes'
import { getProperty } from 'utils/helpers/object'

type useInterpolatedFilteractionParamsProps = {
  filteractionParams?: FilteractionParams[]
  onSourceValuesChange?: (values: Record<string, string | undefined>) => void
  parentPath?: string[]
}

const useSourceActivityValues = ({
  filteractionParams,
  onSourceValuesChange,
  parentPath = []
}: useInterpolatedFilteractionParamsProps) => {
  const [initialSourceValuesSet, setInitialSourceValuesSet] = useState(
    !filteractionParams?.length
  )
  const [hasAllSourceValues, setHasAllSourceValues] = useState(
    !filteractionParams?.length
  )
  const [sourceActivityValues, setSourceActivityValues] = useState<
    Record<string, string | undefined>
  >({})
  const activityValues = useAppSelector(
    (state) => state.interactionModel.flatData?.activity_values
  )

  // ref that holds the latest sourceActivityValues so debounced function
  // can compare against the most recent state without causing re-creation
  const sourceValuesRef =
    useRef<Record<string, string | undefined>>(sourceActivityValues)

  useEffect(() => {
    sourceValuesRef.current = sourceActivityValues
  }, [sourceActivityValues])

  const getHasAllSourceValues = useCallback(
    (values: Record<string, string | undefined>) => {
      if (!filteractionParams?.length) return true

      const hasUndefinedSourceValues =
        !Object.values(values).length ||
        Object.values(values).some((value) => !value)

      return !hasUndefinedSourceValues
    },
    [filteractionParams]
  )

  const updateSourceActivityValues = useMemo(
    () =>
      debounce(
        (
          activityValues: Record<string, ActivityValue | NestableGroupValue>
        ) => {
          if (!filteractionParams?.length) return

          const values = filteractionParams.reduce(
            (acc, param) => {
              const activityValue: ActivityValue | undefined =
                !!parentPath.length
                  ? getProperty(activityValues, [
                      ...parentPath,
                      param.input_value_activity_id
                    ])
                  : (activityValues?.[
                      param.input_value_activity_id
                    ] as ActivityValue)

              acc[param.input_value_activity_id] =
                typeof activityValue === 'object'
                  ? activityValue.id || activityValue.des
                  : activityValue

              return acc
            },
            {} as Record<string, string | undefined>
          )

          if (shallowEqual(sourceValuesRef.current || {}, values)) {
            return
          }

          if (hasAllSourceValues) {
            onSourceValuesChange?.(values)
          }

          setHasAllSourceValues(getHasAllSourceValues(values))
          setSourceActivityValues(values)
          setInitialSourceValuesSet(true)
        },
        350
      ),
    [
      filteractionParams,
      parentPath,
      setInitialSourceValuesSet,
      getHasAllSourceValues,
      onSourceValuesChange,
      hasAllSourceValues
    ]
  )

  useEffect(() => {
    if (!activityValues) return

    updateSourceActivityValues(activityValues)
  }, [updateSourceActivityValues, activityValues])

  return {
    values: sourceActivityValues,
    initialSourceValuesSet: initialSourceValuesSet,
    hasAllSourceValues
  }
}

export default useSourceActivityValues
